import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Alert, Card, Col, Drawer, Input, Radio, Row, Select, Spin, Statistic, Table, Tag,
} from 'antd';
import PropTypes from 'prop-types';
import {
  Bar, BarChart, Cell, Legend, Line, LineChart, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import * as api from 'util/api';
import { pageCatalog, pageTitle } from './pageTitle';
import './Usage.less';

const TYPE_LABEL = {
  page_view: 'Page',
  click: 'Click',
  link: 'Link',
  active: 'Active',
};

const COUNT_LABEL = {
  page: 'Views',
  feature: 'Uses',
  device: 'Events',
  link: 'Clicks',
};

const PIE_COLORS = [
  '#1677ff', '#52c41a', '#fa8c16', '#722ed1', '#13c2c2', '#eb2f96', '#faad14', '#8c8c8c',
];

const titleCase = (value) => {
  if (!value) return '—';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const displayName = (value) => {
  const text = value || 'Other';
  if (text !== text.toLowerCase()) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const shortLabel = (value) => {
  const text = String(value || '');
  if (text.length <= 22) return text;
  return `${text.slice(0, 22)}...`;
};

const sumBy = (rows, key, valueKey) => {
  const map = new Map();
  rows.forEach((row) => {
    const raw = row[key] || 'Other';
    const prev = map.get(raw) || { raw, name: displayName(raw), value: 0 };
    prev.value += row[valueKey] || 0;
    map.set(raw, prev);
  });
  const slices = Array.from(map.values()).sort((a, b) => b.value - a.value);
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);
  return slices.map((slice) => ({ ...slice, total }));
};

const drillTitle = (drill) => {
  if (!drill) return '';
  const { kind, row } = drill;
  if (kind === 'page') return pageTitle(row.path) || row.path || 'Page';
  if (kind === 'feature') return row.label || 'Feature';
  if (kind === 'link') return row.label || row.target || 'Link';
  const parts = [titleCase(row.device), row.os, row.browser]
    .filter((part) => part && part !== '—');
  return parts.join(' · ') || 'Device';
};

const drillParams = (drill) => {
  const { kind, row } = drill;
  if (kind === 'page') return { kind, path: row.path || '' };
  if (kind === 'feature') return { kind, label: row.label || '', path: row.path || '' };
  if (kind === 'link') return { kind, target: row.target || '' };
  return {
    kind,
    device: row.device || '',
    os: row.os || '',
    browser: row.browser || '',
  };
};

const formatWhen = (value) => (value ? dayjs(value).format('D MMM YYYY HH:mm') : '—');

const countCol = (title, key) => ({
  title,
  dataIndex: key,
  align: 'right',
  width: 110,
  sorter: (a, b) => (a[key] || 0) - (b[key] || 0),
});

const pageColumns = [
  { title: 'Page', dataIndex: 'label', ellipsis: true },
  { title: 'Path', dataIndex: 'path', ellipsis: true },
  countCol('Views', 'views'),
  countCol('Users', 'users'),
];

const featureColumns = [
  { title: 'Feature', dataIndex: 'label', ellipsis: true },
  { title: 'Page', dataIndex: 'path', ellipsis: true },
  countCol('Uses', 'uses'),
  countCol('Users', 'users'),
];

const linkColumns = [
  {
    title: 'Link',
    dataIndex: 'target',
    ellipsis: true,
    render: (target, row) => (
      <a
        href={target}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(event) => event.stopPropagation()}
      >
        {row.label || target}
      </a>
    ),
  },
  countCol('Clicks', 'clicks'),
  countCol('Users', 'users'),
];

const deviceColumns = [
  {
    title: 'Device',
    dataIndex: 'device',
    render: titleCase,
  },
  { title: 'OS', dataIndex: 'os', render: (value) => value || '—' },
  { title: 'Browser', dataIndex: 'browser', render: (value) => value || '—' },
  countCol('Events', 'events'),
  countCol('Users', 'users'),
];

const eventColumns = [
  {
    title: 'When',
    dataIndex: 'createdAt',
    width: 160,
    render: formatWhen,
  },
  {
    title: 'Type',
    dataIndex: 'type',
    width: 80,
    render: (type) => TYPE_LABEL[type] || type,
  },
  {
    title: 'Label',
    dataIndex: 'label',
    width: 240,
    ellipsis: true,
    render: (value, row) => (
      row.type === 'page_view' ? (pageTitle(row.path) || '—') : (value || '—')
    ),
  },
  {
    title: 'Path',
    dataIndex: 'path',
    width: 300,
    ellipsis: true,
    render: (value) => value || '—',
  },
  {
    title: 'Active',
    dataIndex: 'activeSeconds',
    width: 80,
    align: 'right',
    render: (value) => (value ? `${value}s` : '—'),
  },
];

const adoptionColumns = [
  {
    title: 'Page',
    dataIndex: 'title',
    ellipsis: true,
    render: (title, row) => (
      <span>
        {title}
        {row.hidden ? <Tag className="usage-page-tag">Hidden</Tag> : null}
        {row.pathOverride ? <Tag className="usage-page-tag">Path override</Tag> : null}
      </span>
    ),
  },
  {
    title: 'Section',
    dataIndex: 'section',
    width: 140,
    ellipsis: true,
    render: (value) => value || '—',
  },
  {
    title: 'Views',
    dataIndex: 'views',
    align: 'right',
    width: 90,
    defaultSortOrder: 'ascend',
    sorter: (a, b) => (a.views || 0) - (b.views || 0),
  },
  countCol('Users', 'users'),
  {
    title: 'Last viewed',
    dataIndex: 'allTimeLastSeen',
    width: 170,
    render: (value) => (value ? formatWhen(value) : 'Never'),
    sorter: (a, b) => {
      const left = a.allTimeLastSeen ? dayjs(a.allTimeLastSeen).valueOf() : 0;
      const right = b.allTimeLastSeen ? dayjs(b.allTimeLastSeen).valueOf() : 0;
      return left - right;
    },
  },
];

const boardTable = {
  size: 'small',
  pagination: { pageSize: 10, hideOnSinglePage: true },
  scroll: { x: 640 },
};

const drawerTable = {
  size: 'small',
  pagination: { pageSize: 20, hideOnSinglePage: true },
  scroll: { x: 640 },
};

const sliceShape = PropTypes.shape({
  raw: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.number,
  total: PropTypes.number,
});

const PieTip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0];
  const value = point.value || 0;
  const total = (point.payload && point.payload.total) || 0;
  const percent = total ? Math.round((value / total) * 100) : 0;
  return (
    <div className="usage-tip">
      {`${point.name}: ${value} (${percent}%)`}
    </div>
  );
};

PieTip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.number,
    payload: PropTypes.shape({
      total: PropTypes.number,
    }),
  })),
};

PieTip.defaultProps = {
  active: false,
  payload: [],
};

const SharePie = ({
  title, slices, active, onSlice,
}) => (
  <Card title={title} className="usage-chart">
    {slices.length === 0 && <div className="usage-empty">Nothing in this range</div>}
    {slices.length > 0 && (
      <ResponsiveContainer width="100%" height={260}>
        <PieChart>
          <Pie
            data={slices}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="45%"
            outerRadius={72}
            onClick={(slice) => onSlice(slice.raw)}
          >
            {slices.map((slice, index) => (
              <Cell
                key={slice.raw}
                fill={PIE_COLORS[index % PIE_COLORS.length]}
                stroke={active === slice.raw ? '#262626' : '#fff'}
                strokeWidth={active === slice.raw ? 2 : 1}
              />
            ))}
          </Pie>
          <Tooltip content={<PieTip />} />
          <Legend verticalAlign="bottom" height={36} />
        </PieChart>
      </ResponsiveContainer>
    )}
  </Card>
);

SharePie.propTypes = {
  title: PropTypes.string.isRequired,
  slices: PropTypes.arrayOf(sliceShape).isRequired,
  active: PropTypes.string.isRequired,
  onSlice: PropTypes.func.isRequired,
};

const RankBars = ({
  title, bare, rows, nameKey, valueKey, metric, onPick,
}) => {
  const names = {};
  const data = rows.slice(0, 8).map((row) => {
    const base = row[nameKey] || row.path || row.target || 'Unknown';
    names[base] = (names[base] || 0) + 1;
    const name = names[base] > 1 ? `${base} (${names[base]})` : base;
    return { ...row, name, value: row[valueKey] || 0 };
  });
  const plot = data.length === 0 ? (
    <div className="usage-empty">Nothing in this range</div>
  ) : (
    <div className="usage-rank-plot">
      <ResponsiveContainer width="100%" height={Math.max(data.length * 36, 120)}>
        <BarChart
          data={data}
          layout="vertical"
          margin={{
            top: 4,
            right: 16,
            left: 8,
            bottom: 4,
          }}
        >
          <XAxis type="number" allowDecimals={false} />
          <YAxis
            type="category"
            dataKey="name"
            width={140}
            reversed
            tick={{ fontSize: 11 }}
            tickFormatter={shortLabel}
          />
          <Tooltip />
          <Bar
            dataKey="value"
            name={metric}
            fill="#1677ff"
            maxBarSize={18}
            onClick={(entry) => onPick(entry)}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
  if (bare) {
    return (
      <div className="usage-rank-bare">
        {title ? <div className="usage-caption">{title}</div> : null}
        {plot}
      </div>
    );
  }
  return <Card title={title} className="usage-chart">{plot}</Card>;
};

RankBars.propTypes = {
  title: PropTypes.string,
  bare: PropTypes.bool,
  rows: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    path: PropTypes.string,
    email: PropTypes.string,
    target: PropTypes.string,
    views: PropTypes.number,
    uses: PropTypes.number,
    pageViews: PropTypes.number,
  })).isRequired,
  nameKey: PropTypes.string.isRequired,
  valueKey: PropTypes.string.isRequired,
  metric: PropTypes.string.isRequired,
  onPick: PropTypes.func.isRequired,
};

RankBars.defaultProps = {
  title: '',
  bare: false,
};

const Usage = () => {
  const [days, setDays] = useState(30);
  const [deviceFilter, setDeviceFilter] = useState(null);
  const [osFilter, setOsFilter] = useState(null);
  const [browserFilter, setBrowserFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [adoptionFilter, setAdoptionFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [drill, setDrill] = useState(null);
  const [eventQuery, setEventQuery] = useState('');
  const [eventType, setEventType] = useState('all');

  const summaryParams = {};
  if (deviceFilter) summaryParams.device = deviceFilter;
  if (osFilter) summaryParams.os = osFilter;
  if (browserFilter) summaryParams.browser = browserFilter;

  const summary = useQuery({
    queryKey: [
      'usage-summary',
      days,
      deviceFilter || '',
      osFilter || '',
      browserFilter || '',
    ],
    queryFn: () => api.getUsageSummary(days, summaryParams),
    keepPreviousData: true,
  });

  const events = useQuery({
    queryKey: ['usage-user', selected && selected.userId, days],
    queryFn: () => api.getUsageUserEvents(selected.userId, days),
    enabled: !!selected,
  });

  const breakdown = useQuery({
    queryKey: [
      'usage-breakdown',
      days,
      drill,
      deviceFilter || '',
      osFilter || '',
      browserFilter || '',
    ],
    queryFn: () => api.getUsageBreakdown(days, {
      ...drillParams(drill),
      ...summaryParams,
    }),
    enabled: !!drill,
  });

  const chartData = useMemo(() => {
    const byDay = {};
    const rows = (summary.data && summary.data.daily) || [];
    rows.forEach((row) => {
      byDay[row.day] = row;
    });
    const points = [];
    for (let i = days - 1; i >= 0; i -= 1) {
      const key = dayjs().subtract(i, 'day').format('YYYY-MM-DD');
      const row = byDay[key];
      points.push({
        day: dayjs(key).format('D MMM'),
        users: row ? row.users : 0,
        pageViews: row ? row.pageViews : 0,
        clicks: row ? row.clicks : 0,
      });
    }
    return points;
  }, [summary.data, days]);

  const hourlyData = useMemo(() => {
    const byHour = {};
    const rows = (summary.data && summary.data.hourly) || [];
    rows.forEach((row) => {
      byHour[row.hour] = row;
    });
    const span = days || 1;
    const average = (value) => Math.round(((value || 0) / span) * 10) / 10;
    return Array.from({ length: 24 }, (_, hour) => {
      const row = byHour[hour] || {};
      return {
        hour: `${String(hour).padStart(2, '0')}:00`,
        users: average(row.userDays),
        pageViews: average(row.pageViews),
        clicks: average(row.clicks),
      };
    });
  }, [summary.data, days]);

  const deviceSlices = useMemo(
    () => sumBy((summary.data && summary.data.devices) || [], 'device', 'events'),
    [summary.data],
  );
  const osSlices = useMemo(
    () => sumBy((summary.data && summary.data.devices) || [], 'os', 'events'),
    [summary.data],
  );
  const browserSlices = useMemo(
    () => sumBy((summary.data && summary.data.devices) || [], 'browser', 'events'),
    [summary.data],
  );

  const catalog = useMemo(() => pageCatalog(), []);

  const adoption = useMemo(() => {
    const source = summary.data || {};
    const counts = new Map((source.pages || []).map((row) => [row.path, row]));
    const seen = new Map((source.pageLastSeen || []).map((row) => [row.path, row.lastSeen]));
    const query = search.trim().toLowerCase();
    return catalog
      .map((entry) => {
        const row = counts.get(entry.path);
        return {
          ...entry,
          views: row ? row.views : 0,
          users: row ? row.users : 0,
          lastSeen: row ? row.lastSeen : null,
          allTimeLastSeen: seen.get(entry.path) || null,
        };
      })
      .filter((row) => {
        if (adoptionFilter === 'unused' && row.views !== 0) return false;
        if (adoptionFilter === 'low' && row.users >= 2) return false;
        if (!query) return true;
        return [row.title, row.section, row.parentTitle, row.path].some(
          (part) => String(part || '').toLowerCase().includes(query),
        );
      })
      .sort((a, b) => (
        (a.views - b.views) || (a.users - b.users) || a.title.localeCompare(b.title)
      ));
  }, [catalog, summary.data, search, adoptionFilter]);

  const filtered = useMemo(() => {
    const source = summary.data || {};
    const query = search.trim().toLowerCase();
    const hit = (parts) => !query || parts.some(
      (part) => String(part || '').toLowerCase().includes(query),
    );
    return {
      users: (source.users || []).filter((row) => hit([row.email, row.device])),
      pages: (source.pages || [])
        .map((row) => ({ ...row, label: pageTitle(row.path) }))
        .filter((row) => hit([row.label, row.path])),
      features: (source.features || []).filter((row) => hit([row.label, row.path])),
      links: (source.links || []).filter((row) => hit([row.label, row.target])),
      devices: (source.devices || []).filter(
        (row) => hit([row.device, row.os, row.browser]),
      ),
    };
  }, [summary.data, search]);

  const chartUsers = useMemo(() => {
    const users = (breakdown.data && breakdown.data.users) || [];
    const names = {};
    return users.slice(0, 15).map((user) => {
      const base = user.email || 'Unknown';
      names[base] = (names[base] || 0) + 1;
      const name = names[base] > 1 ? `${base} (${names[base]})` : base;
      return { name, count: user.count };
    });
  }, [breakdown.data]);

  const visibleEvents = useMemo(() => {
    const rows = (events.data && events.data.events) || [];
    const query = eventQuery.trim().toLowerCase();
    return rows.filter((row) => {
      if (eventType !== 'all' && row.type !== eventType) return false;
      if (!query) return true;
      const label = (
        row.type === 'page_view' ? pageTitle(row.path) : (row.label || '')
      ).toLowerCase();
      const path = (row.path || '').toLowerCase();
      return label.includes(query) || path.includes(query);
    });
  }, [events.data, eventQuery, eventType]);

  const openUser = (record) => {
    setEventQuery('');
    setEventType('all');
    setSelected(record);
  };

  const closeUser = () => {
    setEventQuery('');
    setEventType('all');
    setSelected(null);
  };

  const onDevice = (value) => {
    setDeviceFilter(value === 'all' ? null : value);
    setOsFilter(null);
    setBrowserFilter(null);
  };

  const onSlice = (kind, raw) => {
    if (kind === 'device') {
      setDeviceFilter((prev) => (prev === raw ? null : raw));
      setOsFilter(null);
      setBrowserFilter(null);
      return;
    }
    if (kind === 'os') {
      setOsFilter((prev) => (prev === raw ? null : raw));
      return;
    }
    setBrowserFilter((prev) => (prev === raw ? null : raw));
  };

  const totals = (summary.data && summary.data.totals) || {
    activeUsers: 0,
    sessions: 0,
    pageViews: 0,
    featureUses: 0,
    linkClicks: 0,
    activeMinutes: 0,
  };
  const avgMinutes = totals.activeUsers
    ? Math.round(totals.activeMinutes / totals.activeUsers)
    : 0;

  const userColumns = [
    {
      title: 'User',
      dataIndex: 'email',
      ellipsis: true,
      render: (value) => value || '—',
    },
    {
      title: 'Last seen',
      dataIndex: 'lastSeen',
      width: 170,
      render: formatWhen,
      sorter: (a, b) => dayjs(a.lastSeen).valueOf() - dayjs(b.lastSeen).valueOf(),
    },
    countCol('Days', 'daysActive'),
    countCol('Sessions', 'sessions'),
    countCol('Active min', 'activeMinutes'),
    countCol('Pages', 'pageViews'),
    {
      title: 'Device',
      dataIndex: 'device',
      width: 100,
      render: titleCase,
    },
  ];

  if (summary.isLoading && !summary.data) {
    return (
      <div className="usage-page usage-loading">
        <Spin />
      </div>
    );
  }

  if (summary.isError) {
    return (
      <div className="usage-page">
        <Alert type="error" message="Could not load usage" />
      </div>
    );
  }

  const metric = drill ? COUNT_LABEL[drill.kind] : 'Count';
  const breakdownUsers = (breakdown.data && breakdown.data.users) || [];
  const drillRow = (kind) => (record) => ({
    onClick: () => setDrill({ kind, row: record }),
  });
  const breakdownColumns = [
    {
      title: 'User',
      dataIndex: 'email',
      ellipsis: true,
      render: (value) => value || '—',
    },
    countCol(metric, 'count'),
    {
      title: 'Last seen',
      dataIndex: 'lastSeen',
      width: 170,
      render: formatWhen,
      sorter: (a, b) => dayjs(a.lastSeen).valueOf() - dayjs(b.lastSeen).valueOf(),
    },
  ];
  const topUsers = filtered.users
    .slice()
    .sort((a, b) => (b.pageViews || 0) - (a.pageViews || 0));
  const trackingSince = summary.data && summary.data.trackingSince;
  const trackedOn = trackingSince ? dayjs(trackingSince).format('D MMM YYYY') : null;
  const trackingNote = trackedOn
    ? `Tracking since ${trackedOn}. `
      + 'Zero views only count once tracking covers the selected range.'
    : 'No usage recorded yet.';
  const deviceScoped = Boolean(deviceFilter || osFilter || browserFilter);
  const adoptionNote = [
    trackingNote,
    'Users is how many people opened the page. One visit still counts as used.',
    deviceScoped ? 'Device filters apply, so unused means unused on that device.' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="usage-page">
      <div className="usage-toolbar">
        <Radio.Group value={days} onChange={(event) => setDays(event.target.value)}>
          <Radio.Button value={7}>7 days</Radio.Button>
          <Radio.Button value={30}>30 days</Radio.Button>
          <Radio.Button value={90}>90 days</Radio.Button>
        </Radio.Group>
        <Select
          className="usage-device-select"
          value={deviceFilter || 'all'}
          onChange={onDevice}
        >
          <Select.Option value="all">All devices</Select.Option>
          <Select.Option value="desktop">Desktop</Select.Option>
          <Select.Option value="mobile">Mobile</Select.Option>
          <Select.Option value="tablet">Tablet</Select.Option>
        </Select>
        <Input.Search
          allowClear
          placeholder="Search users, pages, features, links"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        {osFilter && (
          <Tag
            closable
            onClose={(event) => {
              event.preventDefault();
              setOsFilter(null);
            }}
          >
            {osFilter}
          </Tag>
        )}
        {browserFilter && (
          <Tag
            closable
            onClose={(event) => {
              event.preventDefault();
              setBrowserFilter(null);
            }}
          >
            {browserFilter}
          </Tag>
        )}
      </div>

      <Row gutter={[16, 16]} className="usage-kpis">
        <Col xs={12} sm={8} lg={4}>
          <Card><Statistic title="Active users" value={totals.activeUsers} /></Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card><Statistic title="Sessions" value={totals.sessions} /></Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card><Statistic title="Avg active min / user" value={avgMinutes} /></Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card><Statistic title="Page views" value={totals.pageViews} /></Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card><Statistic title="Feature uses" value={totals.featureUses} /></Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card><Statistic title="Link clicks" value={totals.linkClicks} /></Card>
        </Col>
      </Row>

      <Card
        title="Page adoption"
        className="usage-section"
        extra={(
          <Radio.Group
            size="small"
            value={adoptionFilter}
            onChange={(event) => setAdoptionFilter(event.target.value)}
          >
            <Radio.Button value="all">All</Radio.Button>
            <Radio.Button value="unused" title="No views in this range">Unused</Radio.Button>
            <Radio.Button value="low" title="Fewer than 2 users">Low</Radio.Button>
          </Radio.Group>
        )}
      >
        <div className="usage-caption">{adoptionNote}</div>
        <Table
          {...boardTable}
          rowKey="path"
          columns={adoptionColumns}
          dataSource={adoption}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
          onRow={drillRow('page')}
          rowClassName="usage-user-row"
        />
      </Card>

      <Card className="usage-chart" title="Daily activity">
        <div className="usage-chart-plot">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chartData}>
              <XAxis dataKey="day" tick={{ fontSize: 11 }} interval={days > 30 ? 6 : 0} />
              <YAxis yAxisId="left" allowDecimals={false} width={40} />
              <YAxis
                yAxisId="right"
                orientation="right"
                allowDecimals={false}
                width={32}
              />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="pageViews"
                name="Page views"
                stroke="#1677ff"
                dot={false}
                strokeWidth={2}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="clicks"
                name="Feature uses"
                stroke="#52c41a"
                dot={false}
                strokeWidth={2}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="users"
                name="Active users"
                stroke="#722ed1"
                dot={false}
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="usage-chart" title="Average hourly activity">
        <div className="usage-chart-plot">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={hourlyData}>
              <XAxis dataKey="hour" tick={{ fontSize: 11 }} interval={1} />
              <YAxis yAxisId="left" width={40} />
              <YAxis
                yAxisId="right"
                orientation="right"
                width={32}
              />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="pageViews"
                name="Page views"
                stroke="#1677ff"
                dot={false}
                strokeWidth={2}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="clicks"
                name="Feature uses"
                stroke="#52c41a"
                dot={false}
                strokeWidth={2}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="users"
                name="Active users"
                stroke="#722ed1"
                dot={false}
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Row gutter={[16, 16]} className="usage-block">
        <Col xs={24} lg={8}>
          <SharePie
            title="Device"
            slices={deviceSlices}
            active={deviceFilter || ''}
            onSlice={(raw) => onSlice('device', raw)}
          />
        </Col>
        <Col xs={24} lg={8}>
          <SharePie
            title="OS"
            slices={osSlices}
            active={osFilter || ''}
            onSlice={(raw) => onSlice('os', raw)}
          />
        </Col>
        <Col xs={24} lg={8}>
          <SharePie
            title="Browser"
            slices={browserSlices}
            active={browserFilter || ''}
            onSlice={(raw) => onSlice('browser', raw)}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="usage-block">
        <Col xs={24} lg={12}>
          <RankBars
            title="Top pages"
            rows={filtered.pages}
            nameKey="label"
            valueKey="views"
            metric="Views"
            onPick={(row) => setDrill({ kind: 'page', row })}
          />
        </Col>
        <Col xs={24} lg={12}>
          <RankBars
            title="Top features"
            rows={filtered.features}
            nameKey="label"
            valueKey="uses"
            metric="Uses"
            onPick={(row) => setDrill({ kind: 'feature', row })}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="usage-block">
        <Col xs={24} lg={12}>
          <Card title="Users" className="usage-section">
            <RankBars
              bare
              title="Top users by page views"
              rows={topUsers}
              nameKey="email"
              valueKey="pageViews"
              metric="Page views"
              onPick={openUser}
            />
            <Table
              {...boardTable}
              rowKey="userId"
              columns={userColumns}
              dataSource={filtered.users}
              onRow={(record) => ({ onClick: () => openUser(record) })}
              rowClassName="usage-user-row"
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Pages" className="usage-section">
            <Table
              {...boardTable}
              rowKey={(row) => row.path || row.label}
              columns={pageColumns}
              dataSource={filtered.pages}
              onRow={drillRow('page')}
              rowClassName="usage-user-row"
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="usage-block">
        <Col xs={24} lg={12}>
          <Card title="Features" className="usage-section">
            <Table
              {...boardTable}
              rowKey={(row) => `${row.label || ''}|${row.path || ''}`}
              columns={featureColumns}
              dataSource={filtered.features}
              onRow={drillRow('feature')}
              rowClassName="usage-user-row"
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Links" className="usage-section">
            <Table
              {...boardTable}
              rowKey={(row) => row.target || row.label}
              columns={linkColumns}
              dataSource={filtered.links}
              onRow={drillRow('link')}
              rowClassName="usage-user-row"
            />
          </Card>
        </Col>
      </Row>

      <Card title="Devices" className="usage-section">
        <Table
          {...boardTable}
          rowKey={(row) => `${row.device}|${row.os}|${row.browser}`}
          columns={deviceColumns}
          dataSource={filtered.devices}
          onRow={drillRow('device')}
          rowClassName="usage-user-row"
        />
      </Card>

      <Drawer
        title={drillTitle(drill)}
        visible={!!drill}
        width={960}
        onClose={() => setDrill(null)}
      >
        {breakdown.isLoading && <Spin />}
        {breakdown.isError && <Alert type="error" message="Could not load this breakdown" />}
        {breakdown.data && chartUsers.length > 0 && (
          <div className="usage-drill-chart">
            <ResponsiveContainer width="100%" height={Math.max(chartUsers.length * 36, 120)}>
              <BarChart
                data={chartUsers}
                layout="vertical"
                margin={{
                  top: 4,
                  right: 16,
                  left: 8,
                  bottom: 4,
                }}
              >
                <XAxis type="number" allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={200}
                  reversed
                  tick={{ fontSize: 11 }}
                  tickFormatter={shortLabel}
                />
                <Tooltip />
                <Bar dataKey="count" name={metric} fill="#1677ff" maxBarSize={18} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        {breakdown.data && (
          <Table
            {...drawerTable}
            rowKey="userId"
            columns={breakdownColumns}
            dataSource={breakdownUsers}
            onRow={(record) => ({
              onClick: () => openUser({ userId: record.userId, email: record.email }),
            })}
            rowClassName="usage-user-row"
          />
        )}
      </Drawer>

      <Drawer
        title={selected ? (selected.email || 'User') : ''}
        visible={!!selected}
        width={960}
        onClose={closeUser}
      >
        {events.isLoading && <Spin />}
        {events.isError && <Alert type="error" message="Could not load this user" />}
        {events.data && (
          <>
            <div className="usage-drawer-filters">
              <Input.Search
                allowClear
                placeholder="Search label or path"
                value={eventQuery}
                onChange={(event) => setEventQuery(event.target.value)}
              />
              <Select value={eventType} onChange={setEventType}>
                <Select.Option value="all">All types</Select.Option>
                <Select.Option value="page_view">Page</Select.Option>
                <Select.Option value="click">Click</Select.Option>
                <Select.Option value="link">Link</Select.Option>
                <Select.Option value="active">Active</Select.Option>
              </Select>
            </div>
            <Table
              size="small"
              pagination={drawerTable.pagination}
              rowKey="id"
              columns={eventColumns}
              dataSource={visibleEvents}
            />
          </>
        )}
      </Drawer>
    </div>
  );
};

export default Usage;
